import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { 
  FaHome,
  FaShieldAlt,
  FaCalendarAlt,
  FaEye,
  FaDatabase,
  FaShareAlt,
  FaLock,
  FaClock,
  FaUserShield,
  FaCookieBite,
  FaExternalLinkAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaArrowUp,
  FaExclamationTriangle,
  FaFileAlt,
  FaHistory,
} from 'react-icons/fa';
import { title as siteTitle } from '../constants/constant';
import Layout from '../components/layout/Layout';
import BreadcrumbImg from '../assets/images/breadcrumb.png';

// Constants Data
const breadcrumbData = {
  home: { text: "Home", path: "/" },
  current: "Privacy Policy",
  title: "Privacy Policy",
  image: BreadcrumbImg
};

const policyInfo = {
  lastUpdated: "January 1, 2026",
  commitment: "At our school, we are committed to maintaining the privacy and security of all personal information in our care. This policy explains how we handle information in compliance with applicable laws and our educational mission."
};

const quickNavLinks = [
  { id: "introduction", label: "Introduction" },
  { id: "information-collected", label: "Information We Collect" },
  { id: "purpose", label: "Purpose of Collection" },
  { id: "data-sharing", label: "Data Sharing & Disclosure" },
  { id: "data-security", label: "Data Security" },
  { id: "data-retention", label: "Data Retention" },
  { id: "rights", label: "Your Rights" },
  { id: "cookies", label: "Cookies & Tracking" },
  { id: "third-party", label: "Third-Party Links" },
  { id: "updates", label: "Policy Updates" }
];

const policySections = [
  {
    id: "introduction",
    title: "1. Introduction",
    icon: FaShieldAlt,
    content: "Our school is committed to protecting the privacy and security of student, parent, and staff information. This Privacy Policy outlines how we collect, use, disclose, and protect personal information in accordance with applicable laws and regulations.",
    points: [
      "Applies to all information collected through school operations",
      "Covers students, parents, staff, and website visitors",
      "Compliant with education regulations and data protection laws"
    ]
  },
  {
    id: "information-collected",
    title: "2. Information We Collect",
    icon: FaDatabase,
    content: "We collect information necessary for educational and administrative purposes:",
    bulletPoints: [
      "Student Information: Name, date of birth, address, contact details, academic records, medical information, photographs",
      "Parent/Guardian Information: Names, contact details, occupation, identification documents",
      "Staff Information: Personal details, qualifications, employment history, background checks",
      "Website Usage: IP addresses, browser type, pages visited (through cookies)",
      "Financial Information: Fee payment details, bank information"
    ]
  },
  {
    id: "purpose",
    title: "3. Purpose of Collection",
    icon: FaEye,
    content: "We use collected information for legitimate educational and administrative purposes:",
    points: [
      "Student enrollment and academic management",
      "Communication with parents and guardians",
      "Academic assessment and progress tracking",
      "School administration and operations",
      "Safety and security on school premises",
      "Compliance with legal and regulatory requirements"
    ]
  },
  {
    id: "data-sharing",
    title: "4. Data Sharing & Disclosure",
    icon: FaShareAlt,
    content: "We may share information only under specific circumstances:",
    bulletPoints: [
      "With educational authorities as required by law",
      "With service providers (transport, medical, etc.) under confidentiality agreements",
      "During emergencies involving health or safety",
      "For legal compliance or law enforcement requests",
      "With parent consent for specific purposes"
    ]
  },
  {
    id: "data-security",
    title: "5. Data Security",
    icon: FaLock,
    content: "We implement appropriate security measures to protect personal information:",
    points: [
      "Secure storage of physical and digital records",
      "Access controls and authentication measures",
      "Regular security assessments and updates",
      "Staff training on data protection",
      "Encryption of sensitive information"
    ]
  },
  {
    id: "data-retention",
    title: "6. Data Retention",
    icon: FaClock,
    content: "We retain personal information only as long as necessary:",
    bulletPoints: [
      "Student records: Maintained as per education board requirements",
      "Financial records: 7 years as per statutory requirements",
      "Employee records: Duration of employment plus statutory period",
      "Website logs: 3 months for security purposes",
      "Destruction of records: Secure disposal when no longer needed"
    ]
  },
  {
    id: "rights",
    title: "7. Your Rights",
    icon: FaUserShield,
    content: "Parents and eligible students have certain rights regarding their information:",
    points: [
      "Right to access personal information",
      "Right to request correction of inaccurate data",
      "Right to object to certain processing activities",
      "Right to withdraw consent (where applicable)",
      "Right to lodge complaints with authorities"
    ]
  },
  {
    id: "cookies",
    title: "8. Cookies & Tracking",
    icon: FaCookieBite,
    content: "Our website uses cookies to enhance user experience:",
    bulletPoints: [
      "Essential cookies: Required for website functionality",
      "Analytics cookies: Help us understand website usage",
      "Preference cookies: Remember your settings",
      "You can control cookies through browser settings"
    ]
  },
  {
    id: "third-party",
    title: "9. Third-Party Links",
    icon: FaExternalLinkAlt,
    content: "Our website may contain links to third-party sites:",
    points: [
      "We are not responsible for third-party privacy practices",
      "Review privacy policies of linked sites",
      "External sites have their own terms and conditions"
    ]
  },
  {
    id: "updates",
    title: "10. Policy Updates",
    icon: FaHistory,
    content: "We may update this policy periodically:",
    bulletPoints: [
      "Updates will be posted on our website",
      "Significant changes will be communicated",
      "Current version always available online",
      `Last updated: ${policyInfo.lastUpdated}`
    ]
  }
];

const importantNotes = [
  "This policy applies to all school operations including online and offline activities",
  "We never sell personal information to third parties",
  "Photographs may be used for school publications with consent",
  "Medical information is handled with special confidentiality",
  "Parents have primary rights regarding student information"
];

const contactInfo = [
  { icon: FaEnvelope, label: "Email", value: "privacy@school.edu", bgColor: "bg-blue-100", iconColor: "text-blue-600" },
  { icon: FaUserShield, label: "Contact Officer", value: "Data Protection Officer", bgColor: "bg-blue-100", iconColor: "text-blue-600" },
  { icon: FaMapMarkerAlt, label: "Availability", value: "School Office, During Office Hours", bgColor: "bg-blue-100", iconColor: "text-blue-600" }
];

export default function Privacy() {
  // Handle smooth scrolling for anchor links
  useEffect(() => {
    const handleHashChange = () => {
      const { hash } = window.location;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Layout>
      <Helmet>
        <title>Privacy Policy | {siteTitle}</title>
        <meta name="description" content="Read our privacy policy to understand how we collect, use, and protect your personal information at Gyan Rashmi." />
      </Helmet>

      {/* Breadcrumb Section */}
      <div className="relative bg-gray-900">
        <div className="h-40 md:h-48">
          <img 
            alt="Breadcrumb" 
            className="object-cover w-full h-full opacity-60" 
            src={breadcrumbData.image} 
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="flex items-center mb-2 text-sm text-white">
            <a 
              href={breadcrumbData.home.path} 
              className="flex items-center transition-colors hover:text-gray-200"
            >
              <FaHome className="w-3 h-3 mr-1.5" />
              {breadcrumbData.home.text}
            </a>
            <span className="mx-2">/</span>
            <span className="font-semibold">{breadcrumbData.current}</span>
          </div>
          <h1 className="text-2xl font-bold text-white md:text-3xl">{breadcrumbData.title}</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container px-3 py-5 mx-auto max-w-7xl md:px-4 md:py-6">
        {/* Commitment Header */}
        <div className="p-4 mb-5 border border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <FaShieldAlt className="w-4 h-4 text-blue-600" />
            <h2 className="text-base font-bold text-gray-900">Our Commitment to Privacy</h2>
          </div>
          <p className="mb-3 text-xs text-gray-700">{policyInfo.commitment}</p>
          <div className="flex items-center gap-1.5 text-xs text-blue-600">
            <FaCalendarAlt className="w-3 h-3" />
            <span>Last Updated: {policyInfo.lastUpdated}</span>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="p-4 mb-5 border border-gray-200 bg-gray-50 rounded-xl">
          <h3 className="mb-3 text-xs font-bold text-gray-900">Quick Navigation</h3>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 md:grid-cols-5">
            {quickNavLinks.map((link) => (
              <a 
                key={link.id}
                href={`#${link.id}`} 
                className="px-2 py-1.5 text-[10px] text-center text-gray-700 transition-colors bg-white border border-gray-300 rounded-lg hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* Policy Sections */}
        <div className="mb-5 space-y-4">
          {policySections.map((section) => (
            <div key={section.id} id={section.id} className="scroll-mt-20">
              <div className="overflow-hidden transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-sm">
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-100 rounded-lg">
                      <section.icon className="w-4 h-4 text-blue-600" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">{section.title}</h3>
                  </div>
                </div>
                <div className="p-4">
                  <p className="mb-3 text-xs text-gray-700">{section.content}</p>
                  {section.points && (
                    <ul className="space-y-1.5">
                      {section.points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-1.5">
                          <div className="w-1 h-1 mt-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
                          <span className="text-xs text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.bulletPoints && (
                    <ul className="space-y-1.5">
                      {section.bulletPoints.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-1.5">
                          <span className="text-xs font-medium text-blue-600">•</span>
                          <span className="text-xs text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Important Notes */}
        <div className="p-4 mb-5 border bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <FaExclamationTriangle className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-bold text-gray-900">Important Notes</h3>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {importantNotes.map((note, idx) => (
              <div key={idx} className="flex items-start gap-1.5">
                <div className="w-1 h-1 mt-1.5 bg-amber-500 rounded-full flex-shrink-0"></div>
                <p className="text-xs text-gray-700">{note}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="p-4 mb-5 border border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
          <h3 className="mb-4 text-sm font-bold text-center text-gray-900">Contact for Privacy Concerns</h3>
          <div className="grid gap-3 md:grid-cols-3">
            {contactInfo.map((item, idx) => (
              <div key={idx} className="text-center">
                <div className={`inline-flex items-center justify-center w-8 h-8 mb-2 ${item.bgColor} rounded-full`}>
                  <item.icon className={`w-3.5 h-3.5 ${item.iconColor}`} />
                </div>
                <div className="text-[10px] text-gray-600">{item.label}</div>
                <div className="text-xs font-medium text-gray-900">{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="p-4 mb-5 bg-white border border-gray-200 rounded-xl">
          <div className="text-center">
            <h3 className="mb-2 text-sm font-bold text-gray-900">Summary</h3>
            <p className="mb-3 text-xs text-gray-700">
              This Privacy Policy is designed to be transparent about how we handle personal information. 
              We regularly review and update our practices to ensure compliance with laws and best practices in data protection.
            </p>
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-700 bg-gray-100 rounded-lg">
              <FaFileAlt className="w-3 h-3" />
              <span>This policy is part of our school's commitment to transparency and protection</span>
            </div>
          </div>
        </div>

        {/* Back to Top */}
        <div className="text-center">
          <button 
            onClick={scrollToTop}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Back to Top
            <FaArrowUp className="w-3 h-3" />
          </button>
        </div>
      </div>
    </Layout>
  );
}